import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { ComplianceSummary, HeatmapRow, WeekCell } from "../types";
import { exportHeatmapCsv } from "../lib/exportCsv";
import { isCalendarYtd, windowLong } from "../lib/reportingWindow";

// Stable DOM id for a team header row. TeamOrgChart dispatches a
// scrollToTeam event with a team name; this lets the heatmap find the
// matching team row and scroll it into view.
export function heatmapTeamAnchorId(team: string): string {
  return "heatmap-team-" + encodeURIComponent(team);
}

// Stable DOM id for a department header row.
export function heatmapDeptAnchorId(dept: string): string {
  return "heatmap-dept-" + encodeURIComponent(dept);
}

// Collapse-state keys, namespaced so a department and a team that happen
// to share a name don't clash in the persisted collapse map.
const deptKey = (dept: string): string => "d:" + dept;
const teamKey = (dept: string, team: string): string =>
  "t:" + dept + "␟" + team;

function persistCollapsed(next: Record<string, boolean>): void {
  try {
    localStorage.setItem("compliance.collapsed", JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

// Views are grouped by cadence: submission + hours are weekly rules
// (one column per week), month_end + approval are monthly rules (one
// column per month). Keeping a single time granularity per view is what
// stops the "is this a weekly or a month-end problem?" confusion.
type View = "submission" | "approval" | "hours" | "month_end";

const MONTHLY_VIEWS: ReadonlySet<View> = new Set(["month_end", "approval"]);

// How many trailing weeks the weekly views display by default. The payload
// carries the full YTD window; the toggle trims what's shown, and the
// summary column always matches the displayed range ("12 wk" percentage
// in recent mode, YTD in full-year mode).
const RECENT_WEEKS = 12;

type WeekRange = "recent" | "full";

// A single month column. idx points at the representative week column
// in heatmap.weeks whose cells carry this month's state.
interface MonthCol {
  label: string;
  idx: number;
}

interface Props {
  summary: ComplianceSummary;
}

// Department display order matches the headline tiles.
const DEPARTMENT_ORDER = [
  "Curation",
  "Data Science",
  "Bio",
  "Consulting",
  "Technology Implementation",
  "Project Management",
  "Other",
];

interface TeamGroup {
  team: string;
  rows: HeatmapRow[];
}

interface DeptGroup {
  department: string;
  rows: HeatmapRow[];
  teams: TeamGroup[];
}

// Two-level grouping: department -> team -> people. Departments follow
// DEPARTMENT_ORDER (any unknown ones are appended alphabetically);
// teams and people are sorted alphabetically within their parent.
function groupByDepartmentAndTeam(rows: HeatmapRow[]): DeptGroup[] {
  const byDept: Record<string, HeatmapRow[]> = {};
  for (const r of rows) {
    const key = r.department || "Other";
    if (!byDept[key]) byDept[key] = [];
    byDept[key].push(r);
  }

  function buildTeams(deptRows: HeatmapRow[]): TeamGroup[] {
    const byTeam: Record<string, HeatmapRow[]> = {};
    for (const r of deptRows) {
      const key = r.team || "Unassigned";
      if (!byTeam[key]) byTeam[key] = [];
      byTeam[key].push(r);
    }
    return Object.keys(byTeam)
      .sort((a, b) => a.localeCompare(b))
      .map((team) => ({
        team,
        rows: byTeam[team]
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name)),
      }));
  }

  const ordered: DeptGroup[] = [];
  const seen = new Set<string>();
  for (const name of DEPARTMENT_ORDER) {
    if (byDept[name] && byDept[name].length > 0) {
      ordered.push({
        department: name,
        rows: byDept[name],
        teams: buildTeams(byDept[name]),
      });
      seen.add(name);
    }
  }
  for (const name of Object.keys(byDept).sort((a, b) => a.localeCompare(b))) {
    if (seen.has(name)) continue;
    ordered.push({
      department: name,
      rows: byDept[name],
      teams: buildTeams(byDept[name]),
    });
  }
  return ordered;
}

// Parse as UTC and render in UTC so every viewer sees the same date.
// new Date("2026-06-01") is UTC midnight; rendering it in a local zone
// shows May 31 for US viewers and Jun 1 for EU viewers (the "+1" bug).
function fmtWeek(iso: string): string {
  const d = parseIsoDate(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

// Parse "YYYY-MM-DD" as UTC midnight so month/day math is immune to the
// viewer's timezone (new Date("2026-06-01") in a negative-offset zone
// renders as May 31 with toLocaleDateString).
function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1));
}

function monthLabel(d: Date, withYear: boolean): string {
  return d.toLocaleDateString(
    undefined,
    withYear
      ? { month: "short", year: "numeric", timeZone: "UTC" }
      : { month: "short", timeZone: "UTC" },
  );
}

// The month a boundary week's month_end_pass refers to: the month whose
// last day falls inside the week. If the flag sits on a week just after
// the boundary instead (backend variant), fall back to the month before
// the week's start.
function monthEndMonthOfWeek(weekStartIso: string): Date {
  const start = parseIsoDate(weekStartIso);
  for (let i = 0; i < 7; i++) {
    const d = new Date(start.getTime() + i * 86400000);
    const next = new Date(d.getTime() + 86400000);
    if (next.getUTCDate() === 1) return d;
  }
  return new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 0));
}

function submissionCellClass(cell: WeekCell): string {
  switch (cell.label) {
    case "on_time": return "bg-emerald-500";
    case "late": return "bg-amber-400";
    case "not_submitted": return "bg-red-400";
    case "no_entries": return "bg-slate-300";
    case "pending": return "bg-slate-100 border border-dashed border-slate-400";
  }
}

function submissionCellTooltip(name: string, weekIso: string, cell: WeekCell): string {
  const week = fmtWeek(weekIso);
  if (cell.label === "pending") {
    return (
      name + " - week of " + week +
      "\nPending - the submission deadline (11:59pm Pacific Monday) hasn't passed yet, so this week isn't judged."
    );
  }
  const lines: string[] = [name + " - week of " + week, "Status: " + cell.label];
  if (cell.label === "not_submitted" && !cell.submitted_at) {
    lines.push(
      "No submission for this week - includes weeks with no hours logged at all (employees are required to log every week).",
    );
  }
  if (cell.label === "no_entries") {
    lines.push("No hours logged - contractor not engaged that week, excluded from percentages.");
  }
  if (cell.submitted_at) {
    lines.push("Submitted: " + new Date(cell.submitted_at).toLocaleString());
  } else if (cell.submit_inferred) {
    lines.push(
      "Submitted: time not in Beebole's audit log - counted on time, inferred from approval",
    );
  }
  return lines.join("\n");
}

// Per-column team % for the submission view. Excludes people with
// no_entries that week from BOTH numerator and denominator — a
// contractor who wasn't engaged that week shouldn't drag the column %.
// Pure weekly timeliness: month-end has its own view now.
function teamOnTimePctSubmission(
  rows: HeatmapRow[],
  colIdx: number,
): { pct: number; evaluated: number } {
  let onTime = 0;
  let evaluated = 0;
  for (const r of rows) {
    const c = r.values[colIdx];
    if (!c) continue;
    if (c.label === "no_entries" || c.label === "pending") continue;
    evaluated += 1;
    if (c.label === "on_time") onTime += 1;
  }
  return {
    pct: evaluated > 0 ? Math.round((100 * onTime) / evaluated) : 0,
    evaluated,
  };
}

function monthEndCellClass(cell: WeekCell | undefined): string {
  if (cell?.month_end_pass === false) return "bg-red-700";
  if (cell?.month_end_pass === true) return "bg-emerald-500";
  return "bg-slate-300";
}

function monthEndCellTooltip(name: string, month: string, cell: WeekCell | undefined): string {
  const lines: string[] = [name + " - " + month];
  if (cell?.month_end_pass === true) {
    lines.push("Month-end: all entries submitted by 11:59pm Pacific on the last day of the month");
  } else if (cell?.month_end_pass === false) {
    lines.push(
      "MONTH-END MISS - entries not submitted by the month-end deadline: " +
        cell.month_end_missing.join(", "),
    );
  } else {
    lines.push("Month-end: not evaluable (no entries that month, or deadline pending)");
  }
  return lines.join("\n");
}

function teamMonthEndPct(
  rows: HeatmapRow[],
  colIdx: number,
): { pct: number; evaluated: number; miss: number } {
  let pass = 0, miss = 0, evaluated = 0;
  for (const r of rows) {
    const p = r.values[colIdx]?.month_end_pass;
    if (p === null || p === undefined) continue;
    evaluated += 1;
    if (p) pass += 1;
    else miss += 1;
  }
  return {
    pct: evaluated > 0 ? Math.round((100 * pass) / evaluated) : 0,
    evaluated,
    miss,
  };
}

function approvalCellClass(cell: WeekCell): string {
  if (cell.approval_pass === false) return "bg-red-600";
  if (cell.approval_pass === true) return "bg-emerald-500";
  return "bg-slate-300";
}

function approvalCellTooltip(name: string, month: string, cell: WeekCell | undefined): string {
  const lines: string[] = [name + " - " + month];
  if (cell?.approval_pass === true) {
    lines.push("Team-lead approval: on time");
  } else if (cell?.approval_pass === false) {
    lines.push("APPROVAL MISS - " + month + "'s Rancho-billed entries were not approved by the 2nd working day of the next month");
  } else {
    lines.push("Approval: not yet evaluable (deadline pending, or no Rancho hours that month)");
  }
  return lines.join("\n");
}

function teamApprovalPct(rows: HeatmapRow[], colIdx: number): { pct: number; evaluated: number; miss: number } {
  let pass = 0, miss = 0, evaluated = 0;
  for (const r of rows) {
    const cell = r.values[colIdx];
    if (!cell || cell.approval_pass === null) continue;
    evaluated += 1;
    if (cell.approval_pass) pass += 1;
    else miss += 1;
  }
  return {
    pct: evaluated > 0 ? Math.round((100 * pass) / evaluated) : 0,
    evaluated,
    miss,
  };
}

function hoursCellClass(cell: WeekCell): string {
  if (cell.hours_pass === false) return "bg-red-600";
  if (cell.hours_pass === true) return "bg-emerald-500";
  return "bg-slate-300";
}

function fmtHours(h: number | null | undefined): string {
  if (h === null || h === undefined) return "?";
  return (Math.round(h * 100) / 100).toString();
}

function hoursCellTooltip(name: string, weekIso: string, cell: WeekCell): string {
  const week = fmtWeek(weekIso);
  const lines: string[] = [name + " - week of " + week];
  if (cell.hours_pass === true) {
    lines.push(
      "Hours: ok - logged " + fmtHours(cell.hours_logged) +
      "h of " + fmtHours(cell.hours_expected) + "h allotted",
    );
  } else if (cell.hours_pass === false) {
    lines.push(
      "HOURS MISS - logged " + fmtHours(cell.hours_logged) +
      "h of " + fmtHours(cell.hours_expected) +
      "h allotted (40h full-time unless a custom schedule is set in Beebole)",
    );
  } else {
    lines.push("Hours: not evaluable (contractor - exempt; or no entries / no schedule data that week)");
  }
  return lines.join("\n");
}

function teamHoursPct(rows: HeatmapRow[], colIdx: number): { pct: number; evaluated: number; miss: number } {
  let pass = 0, miss = 0, evaluated = 0;
  for (const r of rows) {
    const cell = r.values[colIdx];
    if (!cell || cell.hours_pass === null || cell.hours_pass === undefined) continue;
    evaluated += 1;
    if (cell.hours_pass) pass += 1;
    else miss += 1;
  }
  return {
    pct: evaluated > 0 ? Math.round((100 * pass) / evaluated) : 0,
    evaluated,
    miss,
  };
}

// ---------------------------------------------------------------------------
// Right-hand summary column: percent over the VISIBLE window.
//
// Each view is a single pillar now, so the summary column must measure the
// same pillar as the cells next to it - computed from the same cells. The
// backend's EmployeeRow.ytd_pct / ytd_weeks_on_time are NOT usable here:
// despite the names they track fully-compliant weeks (weekly submission
// AND month-end AND approval AND hours), so a person with every week
// green in this view can still show 0% there (e.g. their team-lead never
// approved - one approval miss fails every week of that month). That
// mismatch is exactly the confusion this column used to cause. If the API
// ever exposes per-pillar YTD per person, this is the place to use it.
// ---------------------------------------------------------------------------

function teamWindowPct(rows: HeatmapRow[], view: View, startIdx: number): number {
  if (rows.length === 0) return 0;
  if (view === "submission") {
    // Mirror the backend rollup: skip no_entries cells in both the
    // numerator AND the denominator. Otherwise contractors who weren't
    // engaged drag the team % down.
    let good = 0;
    let evaluated = 0;
    for (const r of rows) {
      for (const v of r.values.slice(startIdx)) {
        if (v.label === "no_entries" || v.label === "pending") continue;
        evaluated += 1;
        if (v.label === "on_time") good += 1;
      }
    }
    if (evaluated === 0) return 0;
    return Math.round((100 * good) / evaluated);
  }
  if (view === "hours") {
    let good = 0, evaluated = 0;
    for (const r of rows) {
      for (const v of r.values.slice(startIdx)) {
        if (v.hours_pass === null || v.hours_pass === undefined) continue;
        evaluated += 1;
        if (v.hours_pass === true) good += 1;
      }
    }
    if (evaluated === 0) return 0;
    return Math.round((100 * good) / evaluated);
  }
  let good = 0, evaluated = 0;
  for (const r of rows) {
    for (const v of r.values.slice(startIdx)) {
      if (v.approval_pass === null) continue;
      evaluated += 1;
      if (v.approval_pass === true) good += 1;
    }
  }
  if (evaluated === 0) return 0;
  return Math.round((100 * good) / evaluated);
}

function personWindowPct(row: HeatmapRow, view: View, startIdx: number): number {
  const values = row.values.slice(startIdx);
  if (values.length === 0) return 0;
  if (view === "submission") {
    // Exclude weeks the person had no entries at all — a contractor
    // who logged 3 of the 5 weeks they were engaged shows 60%, not
    // (3 / heatmap-window) %.
    let good = 0;
    let evaluated = 0;
    for (const v of values) {
      if (v.label === "no_entries" || v.label === "pending") continue;
      evaluated += 1;
      if (v.label === "on_time") good += 1;
    }
    if (evaluated === 0) return 0;
    return Math.round((100 * good) / evaluated);
  }
  if (view === "hours") {
    const evaluated = values.filter(
      (v) => v.hours_pass !== null && v.hours_pass !== undefined,
    );
    if (evaluated.length === 0) return 0;
    const good = evaluated.filter((v) => v.hours_pass === true).length;
    return Math.round((100 * good) / evaluated.length);
  }
  const evaluated = values.filter((v) => v.approval_pass !== null);
  if (evaluated.length === 0) return 0;
  const good = evaluated.filter((v) => v.approval_pass === true).length;
  return Math.round((100 * good) / evaluated.length);
}

// Trailing-window percentages for the monthly views, computed over month
// columns so each month counts once (counting week cells would weight a
// 5-week month heavier than a 4-week one).
function personMonthlyPct(row: HeatmapRow, cols: MonthCol[], view: View): number {
  let good = 0, evaluated = 0;
  for (const c of cols) {
    const cell = row.values[c.idx];
    const p = view === "month_end" ? cell?.month_end_pass : cell?.approval_pass;
    if (p === null || p === undefined) continue;
    evaluated += 1;
    if (p) good += 1;
  }
  return evaluated > 0 ? Math.round((100 * good) / evaluated) : 0;
}

function teamMonthlyPct(rows: HeatmapRow[], cols: MonthCol[], view: View): number {
  let good = 0, evaluated = 0;
  for (const r of rows) {
    for (const c of cols) {
      const cell = r.values[c.idx];
      const p = view === "month_end" ? cell?.month_end_pass : cell?.approval_pass;
      if (p === null || p === undefined) continue;
      evaluated += 1;
      if (p) good += 1;
    }
  }
  return evaluated > 0 ? Math.round((100 * good) / evaluated) : 0;
}

function pctTextColor(pct: number): string {
  if (pct >= 95) return "text-emerald-600";
  if (pct >= 85) return "text-lime-600";
  if (pct >= 70) return "text-amber-600";
  return "text-red-600";
}

function PersonCell({ cell, view }: { cell: WeekCell | undefined; view: View }) {
  if (!cell) {
    return (
      <span
        className="inline-flex items-center justify-center w-8 h-6 rounded bg-slate-300"
        aria-label="no data"
      />
    );
  }
  if (view === "submission") {
    return (
      <span
        className={"inline-flex items-center justify-center w-8 h-6 rounded " + submissionCellClass(cell)}
        aria-label={cell.label}
      />
    );
  }
  if (view === "month_end") {
    const isMiss = cell.month_end_pass === false;
    return (
      <span
        className={"inline-flex items-center justify-center w-8 h-6 rounded " + monthEndCellClass(cell)}
        aria-label={
          cell.month_end_pass === true ? "month-end ok" :
          cell.month_end_pass === false ? "month-end miss" :
          "month-end not evaluable"
        }
      >
        {isMiss && (
          <span className="text-white text-base font-extrabold leading-none">{"✕"}</span>
        )}
      </span>
    );
  }
  if (view === "hours") {
    const isMiss = cell.hours_pass === false;
    return (
      <span
        className={"inline-flex items-center justify-center w-8 h-6 rounded " + hoursCellClass(cell)}
        aria-label={
          cell.hours_pass === true ? "hours ok" :
          cell.hours_pass === false ? "hours miss" :
          "hours not evaluable"
        }
      >
        {isMiss && (
          <span className="text-white text-base font-extrabold leading-none">{"✕"}</span>
        )}
      </span>
    );
  }
  const isMiss = cell.approval_pass === false;
  return (
    <span
      className={"inline-flex items-center justify-center w-8 h-6 rounded " + approvalCellClass(cell)}
      aria-label={
        cell.approval_pass === true ? "approved" :
        cell.approval_pass === false ? "approval miss" :
        "approval pending"
      }
    >
      {isMiss && (
        <span className="text-white text-base font-extrabold leading-none">{"✕"}</span>
      )}
    </span>
  );
}

function SubmissionLegend() {
  const items = [
    { label: "On time", cls: "bg-emerald-500" },
    { label: "Late", cls: "bg-amber-400" },
    { label: "Not submitted", cls: "bg-red-400" },
    { label: "No entries (contractors)", cls: "bg-slate-300" },
    { label: "Pending (not due yet)", cls: "bg-slate-100 border border-dashed border-slate-400" },
  ];
  return <LegendRow items={items} />;
}

function MonthEndLegend() {
  const items = [
    { label: "Submitted by month-end", cls: "bg-emerald-500" },
    { label: "Pending / N/A", cls: "bg-slate-300" },
    { label: "MONTH-END MISS", cls: "bg-red-700", glyph: "✕", emphasize: true },
  ];
  return <LegendRow items={items} />;
}

function ApprovalLegend() {
  const items = [
    { label: "Approved on time", cls: "bg-emerald-500" },
    { label: "Pending / N/A", cls: "bg-slate-300" },
    { label: "APPROVAL MISS", cls: "bg-red-600", glyph: "✕", emphasize: true },
  ];
  return <LegendRow items={items} />;
}

function HoursLegend() {
  const items = [
    { label: "Allotted hours met", cls: "bg-emerald-500" },
    { label: "N/A", cls: "bg-slate-300" },
    { label: "HOURS MISS", cls: "bg-red-600", glyph: "✕", emphasize: true },
  ];
  return <LegendRow items={items} />;
}

function LegendRow({
  items,
}: {
  items: { label: string; cls: string; glyph?: string; emphasize?: boolean }[];
}) {
  return (
    <div className="flex items-center gap-3 text-xs text-rancho-slate flex-wrap">
      {items.map((it) => (
        <span key={it.label} className="inline-flex items-center gap-1.5">
          <span className={"inline-flex items-center justify-center w-5 h-4 rounded " + it.cls}>
            {it.glyph && (
              <span className="text-white text-[11px] font-extrabold leading-none">
                {it.glyph}
              </span>
            )}
          </span>
          <span className={it.emphasize ? "font-semibold text-red-700" : ""}>
            {it.label}
          </span>
        </span>
      ))}
    </div>
  );
}

export default function Heatmap({ summary }: Props) {
  const weeks = summary.heatmap.weeks;
  const [query, setQuery] = useState("");
  const [view, setView] = useState<View>(() => {
    try {
      const v = localStorage.getItem("compliance.heatmapView");
      return v === "approval" || v === "hours" || v === "month_end"
        ? v
        : "submission";
    } catch {
      return "submission";
    }
  });

  function selectView(v: View) {
    setView(v);
    try {
      localStorage.setItem("compliance.heatmapView", v);
    } catch {
      /* ignore */
    }
  }

  // Deliberately NOT persisted: every page load starts on the recent
  // window so present-day status is the first thing a lead sees.
  const [weekRange, setWeekRange] = useState<WeekRange>("recent");

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? summary.heatmap.rows.filter((r) => r.name.toLowerCase().includes(q))
      : summary.heatmap.rows;
    return groupByDepartmentAndTeam(filtered);
  }, [summary.heatmap.rows, query]);

  // Include the year in month labels only when the window spans two years.
  const multiYear = useMemo(() => {
    if (weeks.length < 2) return false;
    return (
      parseIsoDate(weeks[0]).getUTCFullYear() !==
      parseIsoDate(weeks[weeks.length - 1]).getUTCFullYear()
    );
  }, [weeks]);

  // Month-end view columns: one per month whose month-end deadline falls
  // inside the heatmap window. Prefer the backend's spanning_week mapping
  // (authoritative); fall back to scanning cells for month_end evaluations.
  const monthEndCols = useMemo<MonthCol[]>(() => {
    const weekIdx = new Map(weeks.map((w, i) => [w, i] as const));
    const fromApi: MonthCol[] = [];
    for (const m of summary.monthly_compliance ?? []) {
      if (!m.spanning_week) continue;
      const idx = weekIdx.get(m.spanning_week);
      if (idx === undefined) continue;
      fromApi.push({ label: m.label, idx });
    }
    if (fromApi.length > 0) return fromApi.sort((a, b) => a.idx - b.idx);
    const cols: MonthCol[] = [];
    weeks.forEach((w, i) => {
      const evaluated = summary.heatmap.rows.some(
        (r) => r.values[i] && r.values[i].month_end_pass !== null,
      );
      if (!evaluated) return;
      cols.push({ label: monthLabel(monthEndMonthOfWeek(w), multiYear), idx: i });
    });
    return cols;
  }, [weeks, summary, multiYear]);

  // Approval view columns: approval is a per-(person, month) rule that the
  // backend paints identically across every week of the month, so collapse
  // each calendar month (of the week's Monday) to one representative column.
  const approvalCols = useMemo<MonthCol[]>(() => {
    const cols: MonthCol[] = [];
    let curKey = "";
    weeks.forEach((w, i) => {
      const d = parseIsoDate(w);
      const key = d.getUTCFullYear() + "-" + d.getUTCMonth();
      if (key === curKey) return;
      curKey = key;
      cols.push({ label: monthLabel(d, multiYear), idx: i });
    });
    return cols;
  }, [weeks, multiYear]);

  const isMonthlyView = MONTHLY_VIEWS.has(view);
  const monthCols = view === "month_end" ? monthEndCols : approvalCols;

  // True when the backend ships the entire year-to-date window (it does
  // now), which makes the window percentages genuine YTD figures. Older
  // cached payloads that still cap the heatmap at 12 weeks fall back to
  // "last N weeks" labelling.
  const isFullYtd =
    typeof summary.ytd_weeks === "number" && weeks.length >= summary.ytd_weeks;

  // Weekly views trim the DISPLAYED columns to the trailing RECENT_WEEKS
  // by default so present-day status isn't hidden behind a horizontal
  // scroll that grows all year. Only the visible columns change - every
  // percentage still covers the full shipped window.
  const canTrimWeeks = weeks.length > RECENT_WEEKS;
  const weekStartIdx =
    !isMonthlyView && canTrimWeeks && weekRange === "recent"
      ? weeks.length - RECENT_WEEKS
      : 0;
  const displayWeeks = weeks.slice(weekStartIdx);

  // In full-year mode, open scrolled to the present (the right end);
  // history is a scroll to the left.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (!isMonthlyView && weekRange === "full") {
      el.scrollLeft = el.scrollWidth;
    }
  }, [view, weekRange, isMonthlyView]);

  // Summary-column values, dispatched on the view's cadence. Always the
  // same pillar as the visible cells, always over the DISPLAYED range -
  // "Last 12 wks" shows a 12-week figure, "Full year" shows YTD.
  function rowYtd(row: HeatmapRow): number {
    if (isMonthlyView) return personMonthlyPct(row, monthCols, view);
    return personWindowPct(row, view, weekStartIdx);
  }
  function groupYtd(rows: HeatmapRow[]): number {
    if (isMonthlyView) return teamMonthlyPct(rows, monthCols, view);
    return teamWindowPct(rows, view, weekStartIdx);
  }

  // team name -> department, used to expand the right rows when another
  // component asks us to scroll to a team.
  const teamToDept = useMemo(() => {
    const m: Record<string, string> = {};
    for (const r of summary.heatmap.rows) {
      const t = r.team || "Unassigned";
      if (!(t in m)) m[t] = r.department || "Other";
    }
    return m;
  }, [summary.heatmap.rows]);

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem("compliance.collapsed");
      return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  });

  function toggleKey(key: string) {
    const next = { ...collapsed, [key]: !collapsed[key] };
    setCollapsed(next);
    persistCollapsed(next);
  }

  useEffect(() => {
    function handler(evt: Event) {
      const team = (evt as CustomEvent<{ team: string }>).detail?.team;
      if (!team) return;
      const dept = teamToDept[team];
      // Expand the containing department and team so the row is visible.
      setCollapsed((prev) => {
        const next = { ...prev };
        if (dept) next[deptKey(dept)] = false;
        next[teamKey(dept ?? "Other", team)] = false;
        persistCollapsed(next);
        return next;
      });
      requestAnimationFrame(() => {
        const el = document.getElementById(heatmapTeamAnchorId(team));
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
    window.addEventListener("scrollToTeam", handler);
    return () => window.removeEventListener("scrollToTeam", handler);
  }, [teamToDept]);

  const filtering = query.trim().length > 0;
  const matchCount = filtering ? groups.reduce((acc, g) => acc + g.rows.length, 0) : 0;

  const spanWeekly =
    weekStartIdx > 0
      ? "last " + displayWeeks.length + " weeks"
      : isFullYtd
      ? windowLong(summary)
      : "last " + weeks.length + " weeks";
  const spanMonthly = isFullYtd ? windowLong(summary) : "last " + monthCols.length + " months";
  const headerTitle =
    view === "submission"
      ? "Weekly submission - " + spanWeekly
      : view === "hours"
      ? "Hours worked - " + spanWeekly
      : view === "month_end"
      ? "Month-end submission - " + spanMonthly
      : "Team-lead approvals - " + spanMonthly;

  const headerCopy =
    view === "submission"
      ? "Each cell shows whether that person's week was submitted by midnight Monday Pacific. Month-end close status has moved to its own view under Monthly."
      : view === "hours"
      ? "Each cell shows whether that person logged their allotted hours that week (Sunday-Saturday, matching Beebole) - 40 for full-time, or their custom Beebole schedule. Paid absences count toward the total; company holidays reduce it. Contractors are exempt and show as N/A."
      : view === "month_end"
      ? "One column per month. Each cell shows whether every entry for that month was submitted by 11:59pm Pacific on the month's last day - the most severe miss, since it blocks the monthly close."
      : "One column per month. Each cell shows whether the team-lead approved that month's Rancho-billed entries by the 2nd working day of the following month.";

  // Roll-up cells for a group header row (department or team). Monthly
  // views aggregate one cell per month column; weekly views one per week.
  function aggregateCells(rows: HeatmapRow[], label: string) {
    if (isMonthlyView) {
      return monthCols.map((c, i) => {
        const agg =
          view === "month_end"
            ? teamMonthEndPct(rows, c.idx)
            : teamApprovalPct(rows, c.idx);
        const noun = view === "month_end" ? "month-end" : "approval";
        const tip =
          agg.evaluated === 0
            ? label + " - " + c.label + " - " + noun + " not evaluable"
            : label + " - " + c.label + " - " + agg.pct + "% " +
              (view === "month_end" ? "submitted by month-end" : "approved on time") +
              " (" + agg.evaluated + " evaluated)" +
              (agg.miss > 0 ? " - " + agg.miss + " " + noun + " miss" : "");
        return (
          <td
            key={i}
            className="text-center px-2 py-2.5 border-b border-slate-200"
            title={tip}
          >
            {agg.evaluated === 0 ? (
              <span className="text-xs text-rancho-slate">-</span>
            ) : (
              <span className={"text-xs font-bold " + pctTextColor(agg.pct)}>
                {agg.pct}%
              </span>
            )}
            {agg.miss > 0 && (
              <span className="ml-1 text-[10px] font-extrabold text-red-700">
                x{agg.miss}
              </span>
            )}
          </td>
        );
      });
    }
    return displayWeeks.map((w, di) => {
      const i = weekStartIdx + di;
      if (view === "submission") {
        const { pct, evaluated } = teamOnTimePctSubmission(rows, i);
        const skipped = rows.length - evaluated;
        const tip =
          evaluated === 0
            ? label + " - " + fmtWeek(w) + " - no one logged hours that week"
            : label + " - " + fmtWeek(w) + " - " + pct +
              "% on time (" + evaluated + "/" + rows.length +
              " people logged hours" +
              (skipped > 0 ? "; " + skipped + " no entries, excluded" : "") +
              ")";
        return (
          <td
            key={i}
            className="text-center px-2 py-2.5 border-b border-slate-200"
            title={tip}
          >
            {evaluated === 0 ? (
              <span className="text-xs text-rancho-slate">-</span>
            ) : (
              <span className={"text-xs font-bold " + pctTextColor(pct)}>
                {pct}%
              </span>
            )}
          </td>
        );
      }
      const hr = teamHoursPct(rows, i);
      const tip =
        hr.evaluated === 0
          ? label + " - " + fmtWeek(w) + " - hours not evaluable"
          : label + " - " + fmtWeek(w) + " - " + hr.pct +
            "% met their allotted hours" +
            (hr.miss > 0 ? " - " + hr.miss + " hours miss" : "");
      return (
        <td
          key={i}
          className="text-center px-2 py-2.5 border-b border-slate-200"
          title={tip}
        >
          {hr.evaluated === 0 ? (
            <span className="text-xs text-rancho-slate">-</span>
          ) : (
            <span className={"text-xs font-bold " + pctTextColor(hr.pct)}>
              {hr.pct}%
            </span>
          )}
          {hr.miss > 0 && (
            <span className="ml-1 text-[10px] font-extrabold text-red-700">
              x{hr.miss}
            </span>
          )}
        </td>
      );
    });
  }

  return (
    <section
      className="rounded-2xl bg-white shadow-card border border-slate-200/70 overflow-hidden"
      id="heatmap"
    >
      <header className="px-5 py-4 border-b border-slate-200 flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-base font-bold tracking-tight text-rancho-ink">
            {headerTitle}
          </h2>
          <p className="text-xs text-rancho-slate mt-0.5 max-w-3xl">{headerCopy}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-end gap-3 shrink-0">
            <div className="flex flex-col items-start gap-1">
              <span className="text-[10px] uppercase tracking-[0.12em] text-rancho-slate leading-none">
                Weekly
              </span>
              <div className="inline-flex rounded-pill border border-slate-300 overflow-hidden text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => selectView("submission")}
                  className="px-4 py-1.5 transition-colors"
                  style={{
                    backgroundColor: view === "submission" ? "#FF8800" : "#FFFFFF",
                    color: view === "submission" ? "#FFFFFF" : "#374151",
                  }}
                >
                  Submission
                </button>
                <button
                  type="button"
                  onClick={() => selectView("hours")}
                  className="px-4 py-1.5 border-l border-slate-300 transition-colors"
                  style={{
                    backgroundColor: view === "hours" ? "#FF8800" : "#FFFFFF",
                    color: view === "hours" ? "#FFFFFF" : "#374151",
                  }}
                >
                  Hours
                </button>
              </div>
            </div>
            <div className="flex flex-col items-start gap-1">
              <span className="text-[10px] uppercase tracking-[0.12em] text-rancho-slate leading-none">
                Monthly
              </span>
              <div className="inline-flex rounded-pill border border-slate-300 overflow-hidden text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => selectView("month_end")}
                  className="px-4 py-1.5 transition-colors"
                  style={{
                    backgroundColor: view === "month_end" ? "#FF8800" : "#FFFFFF",
                    color: view === "month_end" ? "#FFFFFF" : "#374151",
                  }}
                >
                  Month end
                </button>
                <button
                  type="button"
                  onClick={() => selectView("approval")}
                  className="px-4 py-1.5 border-l border-slate-300 transition-colors"
                  style={{
                    backgroundColor: view === "approval" ? "#FF8800" : "#FFFFFF",
                    color: view === "approval" ? "#FFFFFF" : "#374151",
                  }}
                >
                  Approvals
                </button>
              </div>
            </div>
            {!isMonthlyView && canTrimWeeks && (
              <div className="flex flex-col items-start gap-1">
                <span className="text-[10px] uppercase tracking-[0.12em] text-rancho-slate leading-none">
                  Range
                </span>
                <div className="inline-flex rounded-pill border border-slate-300 overflow-hidden text-xs font-semibold">
                  <button
                    type="button"
                    onClick={() => setWeekRange("recent")}
                    className="px-4 py-1.5 transition-colors"
                    style={{
                      backgroundColor: weekRange === "recent" ? "#FF8800" : "#FFFFFF",
                      color: weekRange === "recent" ? "#FFFFFF" : "#374151",
                    }}
                  >
                    Last {RECENT_WEEKS} wks
                  </button>
                  <button
                    type="button"
                    onClick={() => setWeekRange("full")}
                    className="px-4 py-1.5 border-l border-slate-300 transition-colors"
                    style={{
                      backgroundColor: weekRange === "full" ? "#FF8800" : "#FFFFFF",
                      color: weekRange === "full" ? "#FFFFFF" : "#374151",
                    }}
                  >
                    {isCalendarYtd(summary) ? "Full year" : "Full window"}
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by name..."
              className="text-xs rounded-pill border border-slate-300 bg-white text-rancho-ink px-3 py-1.5 pr-7 focus:outline-none focus:ring-2 focus:ring-rancho-orange/50 w-48"
            />
            {filtering && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-rancho-slate hover:text-rancho-ink text-xs"
                aria-label="Clear filter"
              >
                x
              </button>
            )}
          </div>
          {view === "submission" ? (
            <SubmissionLegend />
          ) : view === "hours" ? (
            <HoursLegend />
          ) : view === "month_end" ? (
            <MonthEndLegend />
          ) : (
            <ApprovalLegend />
          )}
          <button
            onClick={() => exportHeatmapCsv(summary)}
            className="inline-flex items-center gap-1.5 rounded-pill border border-slate-300 bg-white text-rancho-ink text-xs font-semibold px-3 py-1.5 hover:bg-slate-50"
            title="Download an Excel-compatible CSV of the full heatmap"
          >
            <span aria-hidden>{"↓"}</span>
            Export to Excel
          </button>
        </div>
      </header>

      {filtering && (
        <div className="px-5 py-2 bg-slate-50 border-b border-slate-100 text-xs text-rancho-slate">
          Showing <span className="font-semibold text-rancho-ink">{matchCount}</span>{" "}
          {matchCount === 1 ? "person" : "people"} matching{" "}
          <span className="font-semibold text-rancho-ink">"{query}"</span>
          {matchCount === 0 && " - no matches"}
        </div>
      )}

      <div className="overflow-x-auto scroll-thin" ref={scrollRef}>
        <table className="min-w-full text-xs border-separate border-spacing-0">
          <thead className="sticky top-0 bg-slate-100 z-10">
            <tr>
              <th className="sticky left-0 z-20 bg-slate-100 text-left px-5 py-3 font-semibold text-rancho-slate w-72 text-[11px] uppercase tracking-[0.12em] border-b border-slate-200">
                Department / Team / Person
              </th>
              {isMonthlyView
                ? monthCols.map((c, i) => (
                    <th
                      key={"m-" + i}
                      className="px-2 py-3 font-semibold text-rancho-slate text-center text-[11px] border-b border-slate-200 whitespace-nowrap"
                    >
                      {c.label}
                    </th>
                  ))
                : displayWeeks.map((w) => (
                    <th
                      key={w}
                      className="px-2 py-3 font-semibold text-rancho-slate text-center text-[11px] border-b border-slate-200 whitespace-nowrap"
                    >
                      {fmtWeek(w)}
                    </th>
                  ))}
              <th
                className="sticky right-0 z-20 bg-slate-100 px-3 py-3 font-semibold text-rancho-slate text-right w-16 text-[11px] uppercase tracking-[0.12em] border-b border-slate-200"
                title={
                  isMonthlyView
                    ? "Percent of the " + monthCols.length + " months shown that passed this check (months not evaluable excluded)." +
                      (isFullYtd ? " Covers everything " + windowLong(summary) + "." : "")
                    : "Percent of the " + displayWeeks.length + " weeks displayed that passed this check (weeks with no entries excluded)." +
                      (weekStartIdx === 0 && isFullYtd ? " Covers everything " + windowLong(summary) + "." : "")
                }
              >
                {isMonthlyView
                  ? isFullYtd ? (isCalendarYtd(summary) ? "YTD" : "All") : monthCols.length + " mo"
                  : weekStartIdx === 0 && isFullYtd ? (isCalendarYtd(summary) ? "YTD" : "All") : displayWeeks.length + " wk"}
              </th>
            </tr>
          </thead>
          <tbody>
            {groups.map(({ department, rows: deptRows, teams }) => {
              const deptCollapsed = filtering
                ? false
                : !!collapsed[deptKey(department)];
              return (
                <Fragment key={"dept-" + department}>
                  {/* Level 1: department */}
                  <tr
                    id={heatmapDeptAnchorId(department)}
                    className="bg-slate-200/80 cursor-pointer scroll-mt-20"
                    onClick={() => !filtering && toggleKey(deptKey(department))}
                  >
                    <td className="sticky left-0 z-[1] bg-slate-200 px-5 py-2.5 font-bold text-rancho-ink border-b border-slate-300">
                      <span className="inline-block w-3 mr-1 text-rancho-slate">
                        {deptCollapsed ? "▸" : "▾"}
                      </span>
                      {department}
                      <span className="ml-2 text-rancho-slate font-normal">
                        ({deptRows.length})
                      </span>
                    </td>
                    {aggregateCells(deptRows, department)}
                    <td className="sticky right-0 z-[1] bg-slate-200 text-right px-3 py-2.5 text-rancho-ink font-bold border-b border-slate-300">
                      {groupYtd(deptRows)}%
                    </td>
                  </tr>

                  {!deptCollapsed &&
                    teams.map(({ team, rows: teamRows }) => {
                      const teamCollapsed = filtering
                        ? false
                        : !!collapsed[teamKey(department, team)];
                      return (
                        <Fragment key={"team-" + department + "-" + team}>
                          {/* Level 2: team */}
                          <tr
                            id={heatmapTeamAnchorId(team)}
                            className="bg-slate-100 cursor-pointer scroll-mt-20"
                            onClick={() =>
                              !filtering && toggleKey(teamKey(department, team))
                            }
                          >
                            <td className="sticky left-0 z-[1] bg-slate-100 px-5 py-2 font-semibold text-rancho-ink border-b border-slate-200">
                              <span className="ml-5 inline-block w-3 mr-1 text-rancho-slate">
                                {teamCollapsed ? "▸" : "▾"}
                              </span>
                              {team}
                              <span className="ml-2 text-rancho-slate font-normal">
                                ({teamRows.length})
                              </span>
                            </td>
                            {aggregateCells(teamRows, team)}
                            <td className="sticky right-0 z-[1] bg-slate-100 text-right px-3 py-2 text-rancho-ink font-semibold border-b border-slate-200">
                              {groupYtd(teamRows)}%
                            </td>
                          </tr>

                          {/* Level 3: people */}
                          {!teamCollapsed &&
                            teamRows.map((r) => (
                              <tr
                                key={"p-" + r.person_id}
                                className="hover:bg-slate-50 even:bg-slate-50/40"
                              >
                                <td className="sticky left-0 z-[1] bg-white px-5 py-1.5 text-rancho-ink truncate max-w-[18rem] border-b border-slate-100">
                                  <span className="ml-12">{r.name}</span>
                                </td>
                                {isMonthlyView
                                  ? monthCols.map((c, idx) => (
                                      <td
                                        key={"m-" + idx}
                                        className="text-center px-2 py-1.5 border-b border-slate-100"
                                        title={
                                          view === "month_end"
                                            ? monthEndCellTooltip(r.name, c.label, r.values[c.idx])
                                            : approvalCellTooltip(r.name, c.label, r.values[c.idx])
                                        }
                                      >
                                        <PersonCell cell={r.values[c.idx]} view={view} />
                                      </td>
                                    ))
                                  : r.values.slice(weekStartIdx).map((cell, di) => (
                                      <td
                                        key={weekStartIdx + di}
                                        className="text-center px-2 py-1.5 border-b border-slate-100"
                                        title={
                                          view === "submission"
                                            ? submissionCellTooltip(r.name, weeks[weekStartIdx + di], cell)
                                            : hoursCellTooltip(r.name, weeks[weekStartIdx + di], cell)
                                        }
                                      >
                                        <PersonCell cell={cell} view={view} />
                                      </td>
                                    ))}
                                <td className="sticky right-0 z-[1] bg-white text-right px-3 py-1.5 text-rancho-slate border-b border-slate-100 font-medium">
                                  {rowYtd(r)}%
                                </td>
                              </tr>
                            ))}
                        </Fragment>
                      );
                    })}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
